import React from 'react';
import { Box, HStack, Stack, Text, Tooltip, VStack } from '@chakra-ui/react';
import { ReactComponent as IsPrimaryIcon } from 'assets/icons/successIcon.svg';
import {
  // useAccess,
  useContacts,
  useDeleteContact,
  usePrimaryContact,
} from 'hooks';
import { useTranslation } from 'localization';
import { Contacts, Profile } from 'types';

import { Button, Spinner } from 'components';

import {
  AccountDetail,
  BorderedBox,
  CreateContact,
  // DeleteAccount,
  PersonalDetail,
} from './components';

const ProfileTab: React.FC<{ profile: Profile | undefined }> = ({
  profile,
}) => {
  // const access = useAccess();
  const { t } = useTranslation('component.settings.profile');
  const {
    data: contacts,
    isLoading: contactsLoading,
    refetch: refreshContacts,
  } = useContacts();
  const { mutateAsync: deleteContact, isPending: deleteLoading } =
    useDeleteContact();
  const { mutateAsync: primaryContact, isPending: primaryLoading } =
    usePrimaryContact();

  const account: {
    label: string;
    value: any;
    isEditable?: boolean;
    isHide?: boolean;
    isCopyable: boolean;
  }[] = [
    {
      label: t('userMagixId'),
      value: profile?.id,
      isHide: false,
      isCopyable: true,
    },
    {
      label: t('userIdentifier'),
      value: profile?.identifier_number,
      isHide: profile && ['LD', 'BROKER'].includes(profile?.role.title),
      isCopyable: true,
    },
    {
      label: t('userEmail'),
      value: profile?.email,
      isHide: false,
      isCopyable: false,
    },
    {
      label: t('userPassword'),
      value: '********',
      isEditable: true,
      isHide: false,
      isCopyable: false,
    },
    {
      label: t('userNickname'),
      value: profile?.username,
      isHide: false,
      isCopyable: false,
    },
  ];
  const personal = [
    { label: t('firstName'), value: profile?.profile.first_name },
    { label: t('lastName'), value: profile?.profile.last_name },
  ];

  const ContactTypeList: React.FC<{
    type: 'MOBILE' | 'EMAIL';
    list: Contacts;
  }> = ({ type, list }) => {
    const sectionLabel = type === 'EMAIL' ? t('form.email') : t('form.mobile');
    const addLabel = `${t('add')} ${sectionLabel}`;

    return (
      <Stack w={{ base: '100%', xl: '50%' }} gap={0} minW={0}>
        <HStack
          w="100%"
          alignItems="center"
          justifyContent="space-between"
          pb={3}
          mb={1}
          gap={3}
          borderBottomWidth="1px"
          borderBottomColor="rgba(255,255,255,0.08)"
        >
          <Text fontSize="sm" fontWeight="semibold" color="body">
            {sectionLabel}
          </Text>
          <CreateContact
            buttonTitle={addLabel}
            type={type}
            onSuccess={refreshContacts}
          />
        </HStack>
        {list.map((contact, index) => {
          return (
            <Stack
              key={contact.id}
              flexDir={{ base: 'column', md: 'row' }}
              alignItems={{ base: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              py={3}
              gap={3}
              borderBottomWidth={index < list.length - 1 ? '1px' : 0}
              borderBottomColor="rgba(255,255,255,0.06)"
            >
              <HStack alignItems="center" gap={2} minW={0} flex="1">
                <Text
                  fontSize="sm"
                  color="body"
                  textTransform="lowercase"
                  wordBreak="break-word"
                >
                  {contact.identity}
                </Text>
                {contact.is_primary && (
                  <Tooltip.Root
                    openDelay={200}
                    positioning={{ placement: 'top' }}
                  >
                    <Tooltip.Trigger>
                      <Box
                        as="span"
                        display="inline-flex"
                        alignItems="center"
                        cursor="default"
                        flexShrink={0}
                      >
                        <IsPrimaryIcon width={20} />
                      </Box>
                    </Tooltip.Trigger>
                    <Tooltip.Positioner>
                      <Tooltip.Content>{t('primaryContact')}</Tooltip.Content>
                    </Tooltip.Positioner>
                  </Tooltip.Root>
                )}
              </HStack>
              <HStack
                flexWrap="wrap"
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
                w={{ base: '100%', md: 'auto' }}
              >
                {contact.status === 'NOT_VERIFIED' && (
                  <Text fontSize="xs" color="warning">
                    {t('notVerified')}
                  </Text>
                )}
                {contact.status === 'VERIFIED' && !contact.is_primary && (
                  <Button
                    fontSize="xs"
                    variant="ghost"
                    title={t('setPrimary')}
                    p={2}
                    isLoading={primaryLoading}
                    onClick={() =>
                      primaryContact({ id: contact.id }).then(() => {
                        refreshContacts();
                      })
                    }
                  />
                )}
                {contact.status === 'NOT_VERIFIED' && (
                  <CreateContact
                    buttonTitle={t('resend')}
                    type={type}
                    verifyId={contact.id}
                    onSuccess={refreshContacts}
                  />
                )}
                {!contact.is_primary && (
                  <Button
                    fontSize="xs"
                    variant="ghost"
                    color="warning"
                    title={t('delete')}
                    p={2}
                    onClick={() =>
                      deleteContact({ id: contact.id || '' }).then(() => {
                        refreshContacts();
                      })
                    }
                    isLoading={deleteLoading}
                  />
                )}
              </HStack>
            </Stack>
          );
        })}
      </Stack>
    );
  };

  return (
    <VStack w="100%" align="stretch" gap={4} my={{ base: 4, md: 8 }}>
      <Stack
        w="100%"
        flexDir={{ base: 'column', xl: 'row' }}
        alignItems="stretch"
        justifyContent="space-between"
        gap={4}
      >
        <AccountDetail title={t('accSetting')} list={account} />
        <PersonalDetail title={t('personalInfo')} list={personal} />
      </Stack>
      <BorderedBox title={t('yourContact')} w="100%">
        {contactsLoading && (
          <Box
            w="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            minH="120px"
          >
            <Spinner />
          </Box>
        )}
        {contacts && !contactsLoading && (
          <Stack
            w="100%"
            alignItems="flex-start"
            justifyContent="space-between"
            flexDir={{ base: 'column', xl: 'row' }}
            gap={{ base: 8, xl: 10 }}
          >
            <ContactTypeList
              list={
                contacts?.list.filter((item) => item.type === 'EMAIL') || []
              }
              type="EMAIL"
            />
            <ContactTypeList
              list={
                contacts?.list.filter((item) => item.type === 'MOBILE') || []
              }
              type="MOBILE"
            />
          </Stack>
        )}
      </BorderedBox>
      {/* {access.meDeletePermission && <DeleteAccount />} */}
    </VStack>
  );
};

export default ProfileTab;
